import { useState } from 'react';
import {
  useCancelSubscription,
  useMySubscription,
  usePlanBalance,
  usePlans,
  useSubscribeToPlan,
  usePaySubscriptionPix,
  useSubscribeByPix,
} from '@/hooks/use-catalog';
import { PageHeader } from '@/components/layout/app-shell';
import { PixPayment } from '@/components/pix-payment';
import {
  ApiError,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Feedback,
  SectionTitle,
  Skeleton,
  formatDate,
  formatMoney,
  useNow,
  useTenant,
} from '@barber/ui';
import type { PixCharge, PublicPlan } from '@barber/ui';
import { Check, Star } from 'lucide-react';

/**
 * Meu plano.
 *
 * ---
 *
 * DUAS TELAS NUMA, PORQUE SÃO DUAS SITUAÇÕES
 *
 * Quem tem plano quer saber quanto ainda pode usar neste ciclo. Quem não tem
 * quer saber se vale a pena. Separá-las em rotas diferentes obrigaria o app a
 * adivinhar para onde mandar o cliente antes de ter a resposta da API.
 *
 * ---
 *
 * O PAGAMENTO ACONTECE NO MERCADO PAGO, E NÃO AQUI
 *
 * Contratar cria a assinatura como PENDENTE e devolve um link. Nenhum dado de
 * cartão passa por este app — o que mantém o escopo PCI inteiro fora do
 * projeto.
 */
export function PlanPage() {
  const { tenant, locale } = useTenant();

  const assinatura = useMySubscription();
  const saldo = usePlanBalance();
  const planos = usePlans();

  const contratar = useSubscribeToPlan();
  const contratarPix = useSubscribeByPix();
  const pagarPix = usePaySubscriptionPix();
  const cancelar = useCancelSubscription();
  const pix = tenant.booking.pix;
  // A janela de 3 dias da renovação pode abrir com a tela aberta.
  const agora = useNow(60_000);
  /** O PIX da mensalidade, depois de gerado. */
  const [cobranca, setCobranca] = useState<PixCharge | null>(null);

  const [erro, setErro] = useState<string | null>(null);
  const [confirmandoCancelamento, setConfirmandoCancelamento] = useState(false);

  const atual = assinatura.data?.subscription ?? null;
  const ativa = atual?.status === 'ACTIVE';
  const pendente = atual?.status === 'PENDING' || atual?.status === 'PAST_DUE';

  async function assinar(plano: PublicPlan): Promise<void> {
    setErro(null);

    try {
      const { checkoutUrl } = await contratar.mutateAsync(plano.id);
      // Navegação de primeiro nível, e não uma nova aba: o retorno do Mercado
      // Pago precisa voltar para ESTE app, e uma aba órfã deixaria o cliente
      // sem saber onde ficou o resultado.
      window.location.assign(checkoutUrl);
    } catch (error) {
      setErro(
        error instanceof ApiError
          ? error.message
          : 'Não conseguimos abrir o pagamento. Tente novamente.',
      );
    }
  }

  /** Trata o retorno do PIX: QR para pagar, ou já pago com o crédito do cliente. */
  function mostrarPix(resultado: PixCharge | { paidWithCredit: true }): void {
    if ('chargeId' in resultado) setCobranca(resultado);
  }

  async function assinarPorPix(plano: PublicPlan): Promise<void> {
    setErro(null);
    try {
      mostrarPix(await contratarPix.mutateAsync(plano.id));
    } catch (error) {
      setErro(error instanceof ApiError ? error.message : 'Não conseguimos gerar o PIX agora.');
    }
  }

  async function pagarMensalidadePorPix(): Promise<void> {
    setErro(null);
    try {
      mostrarPix(await pagarPix.mutateAsync());
    } catch (error) {
      setErro(error instanceof ApiError ? error.message : 'Não conseguimos gerar o PIX agora.');
    }
  }

  async function cancelarAssinatura(): Promise<void> {
    if (!atual) return;

    setErro(null);

    try {
      await cancelar.mutateAsync({ id: atual.id });
      setConfirmandoCancelamento(false);
    } catch (error) {
      setErro(error instanceof ApiError ? error.message : 'Não conseguimos cancelar agora.');
    }
  }

  const carregando = assinatura.isPending || saldo.isPending;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Meu plano" />

      {carregando && <Skeleton className="h-32" />}

      {/*
        403 aqui não é falha: é a resposta certa para quem não é CLIENTE nesta
        barbearia. Quem provisiona uma barbearia com o próprio telefone vira
        administrador dela, entra no app do cliente para ver como ficou, e cai
        exatamente neste ponto. "Não foi possível carregar" mandaria essa pessoa
        procurar um problema que não existe.
      */}
      {assinatura.isError &&
        (assinatura.error instanceof ApiError && assinatura.error.status === 403 ? (
          <EmptyState
            title="Esta área é do cliente"
            description={
              'Sua conta nesta barbearia não é de cliente, então não há plano a mostrar. ' +
              'Se você administra a barbearia, o painel dela é outro endereço.'
            }
          />
        ) : (
          <ErrorState
            description="Não conseguimos carregar seu plano."
            onRetry={() => void assinatura.refetch()}
          />
        ))}

      {erro && <Feedback tone="error">{erro}</Feedback>}

      {cobranca && (
        <PixPayment
          charge={cobranca}
          onPaid={() => {
            void assinatura.refetch();
            void saldo.refetch();
          }}
        />
      )}

      {/* ------------------------------------------------ com assinatura -- */}
      {atual && (
        <Card className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-lg font-bold text-content">{atual.plan.name}</p>
              <p className="text-sm text-content-muted">
                {formatMoney(atual.priceCents, locale)} por mês
              </p>
            </div>

            <Badge tone={ativa ? 'success' : pendente ? 'warning' : 'neutral'}>
              {ativa ? 'Ativo' : pendente ? 'Aguardando pagamento' : 'Cancelado'}
            </Badge>
          </div>

          {ativa && atual.currentPeriodEnd && (
            <p className="text-sm text-content-muted">
              Ciclo atual até {formatDate(atual.currentPeriodEnd, locale)}.
            </p>
          )}

          {/*
            Plano por PIX: sem cartão, cada mensalidade é um PIX. Na
            contratação, no atraso, ou a partir de 3 dias antes de vencer.
          */}
          {atual.paymentMethod === 'PIX' &&
            !cobranca &&
            (pendente ||
              (ativa &&
                atual.currentPeriodEnd &&
                new Date(atual.currentPeriodEnd).getTime() - agora <= 3 * 86_400_000)) && (
              <Button
                fullWidth
                loading={pagarPix.isPending}
                onClick={() => void pagarMensalidadePorPix()}
              >
                Pagar mensalidade por PIX
              </Button>
            )}

          {pendente && atual.checkoutUrl && (
            <>
              <p className="text-sm text-content">
                Seu plano começa a valer assim que o pagamento for confirmado.
              </p>
              <Button fullWidth onClick={() => window.location.assign(atual.checkoutUrl!)}>
                Finalizar pagamento
              </Button>
            </>
          )}

          {saldo.data?.quotas.length ? (
            <div className="flex flex-col gap-3 border-t border-border pt-4">
              <SectionTitle>O que você ainda tem neste ciclo</SectionTitle>

              {saldo.data.quotas.map((quota) => (
                <div key={quota.benefitId} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-content">{quota.label}</span>
                    <span className="font-semibold text-content">
                      {quota.remaining} de {quota.total}
                    </span>
                  </div>

                  <div
                    className="h-2 overflow-hidden rounded-full bg-surface-muted"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={quota.total}
                    aria-valuenow={quota.remaining}
                    aria-label={`${quota.label}: ${quota.remaining} de ${quota.total} restantes`}
                  >
                    <div
                      className="h-full rounded-full bg-brand"
                      style={{
                        width: `${quota.total > 0 ? (quota.remaining / quota.total) * 100 : 0}%`,
                      }}
                    />
                  </div>

                  {/* Reservado já conta: quem marcou já segurou a vaga. */}
                  {quota.used > 0 && (
                    <p className="text-xs text-content-muted">
                      {quota.used} já {quota.used === 1 ? 'usado' : 'usados'} ou reservado neste
                      ciclo
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : null}

          {saldo.data?.discounts.map((desconto) => (
            <p
              key={desconto.label}
              className="flex items-center gap-1.5 text-sm text-content-muted"
            >
              <Check className="h-4 w-4 shrink-0 text-sx-success" aria-hidden="true" />
              {desconto.description}
            </p>
          ))}

          {(ativa || pendente) && (
            <div className="border-t border-border pt-4">
              {confirmandoCancelamento ? (
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-content">
                    Cancelar o plano? Os benefícios valem até o fim do ciclo já pago.
                  </p>

                  <div className="flex gap-2">
                    <Button
                      variant="danger"
                      fullWidth
                      loading={cancelar.isPending}
                      onClick={() => void cancelarAssinatura()}
                    >
                      Sim, cancelar
                    </Button>
                    <Button
                      variant="secondary"
                      fullWidth
                      onClick={() => setConfirmandoCancelamento(false)}
                    >
                      Manter plano
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="ghost" onClick={() => setConfirmandoCancelamento(true)}>
                  Cancelar plano
                </Button>
              )}
            </div>
          )}
        </Card>
      )}

      {/* ------------------------------------------------ sem assinatura -- */}
      {assinatura.isSuccess && !atual && (
        <section className="flex flex-col gap-4">
          {planos.isPending && <Skeleton className="h-40" />}

          {planos.isSuccess && planos.data.length === 0 && (
            <EmptyState
              icon={Star}
              title="Esta barbearia ainda não tem planos"
              description="Quando houver, eles aparecem aqui."
            />
          )}

          {planos.data?.map((plano) => (
            <Card
              key={plano.id}
              className={plano.highlight ? 'border-brand ring-1 ring-brand' : undefined}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-lg font-bold text-content">{plano.name}</p>
                  {plano.description && (
                    <p className="mt-1 text-sm text-content-muted">{plano.description}</p>
                  )}
                </div>

                {plano.highlight && <Badge tone="brand">Mais escolhido</Badge>}
              </div>

              <p className="mt-3 text-2xl font-bold text-content">
                {formatMoney(plano.priceCents, locale)}
                <span className="text-sm font-normal text-content-muted"> /mês</span>
              </p>

              <ul className="mt-3 flex flex-col gap-1.5">
                {plano.benefits.map((beneficio) => (
                  <li key={beneficio.id} className="flex gap-2 text-sm text-content">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-sx-success" aria-hidden="true" />
                    {beneficio.label}
                  </li>
                ))}
              </ul>

              <Button
                fullWidth
                className="mt-4"
                loading={contratar.isPending}
                onClick={() => void assinar(plano)}
              >
                Assinar
              </Button>

              {pix?.available && (
                <Button
                  fullWidth
                  variant="secondary"
                  className="mt-2"
                  loading={contratarPix.isPending}
                  onClick={() => void assinarPorPix(plano)}
                >
                  Assinar por PIX com {pix.discountPercent}% de desconto
                </Button>
              )}
            </Card>
          ))}

          <p className="text-center text-xs text-content-muted">
            O pagamento é processado pelo Mercado Pago. Você pode cancelar quando quiser.
          </p>
        </section>
      )}
    </div>
  );
}
